/*
 * The BetterFX page's "old way vs BetterFX" section
 * (sections/betterfx-race.liquid). Two small Premiere windows play at the
 * same time, both starting with the same clip selected, for three examples
 * in turn on the Interview clip: Gaussian Blur, Mosaic, and Soft Vignette,
 * a custom preset the editor saved in a "My Presets" bin.
 *
 *   Left  - the Effects panel: Effects tab, twirl open the effect's folder
 *           (Video Effects or Presets) and its sub-folder, scroll to the
 *           effect (or to the sub-folder, when that is the one below the
 *           fold), drag it onto the clip. Five steps.
 *   Right - BetterFX: Alt+Space (⌥Space on a Mac) opens the bar centred on
 *           the pointer with the recent effects, a few letters are typed and
 *           the effect is on top, Enter applies it and the bar hides at once.
 *           Three steps.
 *
 * Each action lights its step under the window and the counter counts it.
 * BetterFX finishes first and shows Done while the left is still going;
 * when both are done they hold, fade, and the next example plays. The pills
 * above the windows show which example is playing (and how far it is) and
 * play one when clicked.
 *
 * Plays only while on screen and while the tab is visible; leaving stops it
 * and it starts that example over next time. With reduced motion both
 * windows show how the example ends.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);

  // What the BetterFX bar lists, ranked the way the plugin ranks them
  // (ranking.ts, with Warp Stabilizer, Crop, Transform, Ultra Key and Drop
  // Shadow as the recent effects): an empty query shows the recent
  // effects; then exact > prefix > word prefix > initials ("gb" is Gaussian
  // Blur) > contains > letter by letter, grouped by kind. A [label] is a
  // category row; rows under CUSTOM PRESETS carry the "custom preset" tag.
  const V = ['VIDEO EFFECTS'];
  const A = ['AUDIO EFFECTS'];
  const P = ['CUSTOM PRESETS'];
  const LISTS = {
    '': [['RECENT EFFECTS'], 'Warp Stabilizer', 'Crop', 'Transform', 'Ultra Key', 'Drop Shadow'],
    g: [V, 'Grid', A, 'Guitar Suite', V, 'Gaussian Blur', 'Gradient Wipe', 'Gamma Correction'],
    gb: [V, 'Gaussian Blur', 'RGB Curves', 'RGB Color Corrector', 'Color Balance (RGB)', A, 'Single-band Compressor', 'Graphic Equalizer (10 Bands)'],
    m: [V, 'Mirror', 'Mosaic', 'Unsharp Mask', A, 'Multiband Compressor', 'Tube-modeled Compressor'],
    mo: [V, 'Mosaic', 'Mirror', 'Compound Blur', 'Gamma Correction', A, 'Tube-modeled Compressor'],
    mos: [V, 'Mosaic', A, 'Dynamics Processing', 'Multiband Compressor', 'Tube-modeled Compressor'],
    v: [V, 'Vertical Flip', 'Video Limiter', 'Invert', 'Wave Warp', A, 'Channel Volume', P, 'Soft Vignette'],
    vi: [V, 'Video Limiter', 'Vertical Flip', P, 'Soft Vignette'],
    vig: [P, 'Soft Vignette'],
  };

  // The three examples: the effect, the Effects panel folders that hold
  // it, what the Program monitor shows once it is on, and what is typed. When
  // the sub-folder itself is below the fold the list scrolls to it first.
  const ROUNDS = [
    { name: 'Gaussian Blur', top: 'video', sub: 'blur', look: 'blur', typed: 'gb' },
    { name: 'Mosaic', top: 'video', sub: 'stylize', look: 'mosaic', typed: 'mos', scrollFirst: true },
    { name: 'Soft Vignette', top: 'presets', sub: 'mine', look: 'vignette', typed: 'vig', scrollFirst: true },
  ];

  // The Program monitor's picture in Mosaic's default 10 x 10 blocks: each
  // block's average colour, row by row (the picture is 160 x 90).
  const MOSAIC =
    '34437a34437a34437a34437a34437a34437a34437a34437a34437a34437a4f497a4f497a4f497a4f497a4f497a4f497a4f497a4f497a4f497a4f497a' +
    '694f7a694f7a694f7a694f7a694f7a694f7a694f7a694f7a694f7a694f7a84547a84547a84547a84547a6d497284547a84547a84547a84547a84547a' +
    '9e5a7a8851749b59797b4b703e305d915877d2a090a25f7b9e5a7a9e5a7aab5c7745335f643f6647345f3b2f5c624d68e3bf976c4a6ab15e78b76179' +
    '624063372c5632294f3b2f5c352b53221d38372d4d3b2f5c573b61a45d6a31284d201c351f1b3326203d201c341f1b331f1b3328214026203e1f1b33' +
    '1f1b331f1b331f1b331f1b331f1b331f1b331f1b331f1b331f1b331f1b33151321151321151321151321151321151321151321151321151321151321';
  const mosaicSvg = () => {
    let rects = '';
    for (let i = 0; i < 100; i += 1) {
      const x = (i % 10) * 16;
      const y = Math.floor(i / 10) * 9;
      rects += `<rect x="${x}" y="${y}" width="16.4" height="9.4" fill="#${MOSAIC.slice(i * 6, i * 6 + 6)}"/>`;
    }
    return `<g class="br-mosaic" shape-rendering="crispEdges">${rects}</g>`;
  };

  // The plugin's sizes (lib.rs, styles.css): 360px wide, 54px collapsed,
  // 26px category rows, 40px results, up to 2 categories and 5 results tall.
  const BAR_W = 360;
  const BAR_COLLAPSED = 54;
  const BAR_MAX = 54 + 2 * 26 + 5 * 40 + 3;

  // The Effects panel's timings (ms). The left side is the slower one, so
  // these set how long an example plays (the pill fills over that time).
  const T = { start: 250, tab: 760, tabRead: 380, top: 520, topRead: 480, sub: 420, subRead: 520, box: 360, scrolled: 700, fx: 520, grab: 160, drag: 1050, drop: 260, settle: 450 };
  const MOVES = 6; // each move waits 60ms more than it takes
  const CLICKS = 4; // each click takes 160ms
  const OLD_MS = Object.values(T).reduce((a, b) => a + b, 0) + MOVES * 60 + CLICKS * 160;
  const HOLD = 700;
  const FADE = 320;
  const GAP = 250;

  const init = (root) => {
    if (root.dataset.bfxRaceReady) return;
    root.dataset.bfxRaceReady = 'true';

    const stage = root.querySelector('[data-br-stage]');
    const oldSide = root.querySelector('[data-br-side="old"]');
    const newSide = root.querySelector('[data-br-side="new"]');
    if (!stage || !oldSide || !newSide) return;

    const one = stage.dataset.step || 'step';
    const many = stage.dataset.steps || 'steps';
    // the bar's category rows and tag, in the page's language (effect names stay as they are)
    const catLabel = {
      [V[0]]: root.dataset.i18nCatVideo,
      [A[0]]: root.dataset.i18nCatAudio,
      [P[0]]: root.dataset.i18nCatPreset,
      'RECENT EFFECTS': root.dataset.i18nCatRecent,
    };
    const tagPreset = root.dataset.i18nTagPreset || 'custom preset';

    if (!isMac) root.querySelectorAll('[data-br-mod]').forEach((el) => (el.textContent = 'Alt'));
    root.querySelectorAll('.br-screen svg').forEach((svg) => svg.insertAdjacentHTML('beforeend', mosaicSvg()));

    const parts = (side) => {
      const q = (sel) => side.querySelector(sel);
      return {
        side,
        win: q('[data-br-win]'),
        cursor: q('[data-br-cursor]'),
        count: q('[data-br-count]'),
        unit: q('[data-br-unit]'),
        clip: q('[data-br-clip]'),
        newRows: [...side.querySelectorAll('[data-br-new]')],
        stepLists: [...side.querySelectorAll('[data-br-steps]')],
        steps: [],
      };
    };
    const L = parts(oldSide);
    const R = parts(newSide);
    const tabProject = oldSide.querySelector('[data-br-tab="project"]');
    const tabEffects = oldSide.querySelector('[data-br-tab="effects"]');
    const viewProject = oldSide.querySelector('[data-br-view="project"]');
    const viewEffects = oldSide.querySelector('[data-br-view="effects"]');
    const opens = [...oldSide.querySelectorAll('[data-br-open]')];
    const allKids = [...oldSide.querySelectorAll('[data-br-kids]')];
    const targets = [...oldSide.querySelectorAll('[data-br-target]')];
    const treebox = oldSide.querySelector('[data-br-treebox]');
    const tree = oldSide.querySelector('[data-br-tree]');
    const ghost = oldSide.querySelector('[data-br-ghost]');
    const bar = newSide.querySelector('[data-br-bar]');
    const query = newSide.querySelector('[data-br-q]');
    const placeholder = newSide.querySelector('[data-br-ph]');
    const results = newSide.querySelector('[data-br-results]');
    const picks = [...root.querySelectorAll('[data-br-pick]')];
    const allKeys = [...newSide.querySelectorAll('kbd')];

    let run = 0;
    let playing = false;
    let onScreen = false;
    let animated = false;
    let round = 0;
    let keys = [];

    const node = (key) => oldSide.querySelector(`[data-br-open="${key}"]`);
    const kidsOf = (key) => oldSide.querySelector(`[data-br-kids="${key}"]`);
    const target = (k) => oldSide.querySelector(`[data-br-target="${k}"]`);

    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run ? resolve() : reject(STOP)), ms);
      });

    // Where an element sits inside its window, in px.
    const spot = (p, el, fx = 0.5, fy = 0.5) => {
      const w = p.win.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      return [r.left - w.left + r.width * fx, r.top - w.top + r.height * fy];
    };
    // The arrow's tip sits about 3px in from the cursor box's corner.
    const point = (p, x, y) => {
      p.cursor.style.setProperty('--cx', `${x - 3}px`);
      p.cursor.style.setProperty('--cy', `${y - 2}px`);
    };
    const moveTo = (r, p, el, fx, fy, ms = 600) => {
      const [x, y] = spot(p, el, fx, fy);
      p.cursor.style.transitionDuration = `${ms}ms, 200ms`;
      point(p, x, y);
      return wait(r, ms + 60);
    };
    const click = (r, p) => {
      p.cursor.classList.remove('is-click');
      void p.cursor.offsetWidth;
      p.cursor.classList.add('is-click');
      return wait(r, 160);
    };
    const rest = (p) => {
      // both pointers start on the selected clip
      p.cursor.classList.add('is-instant');
      const [x, y] = spot(p, p.clip, 0.62, 0.55);
      point(p, x, y);
      void p.cursor.offsetWidth;
      p.cursor.classList.remove('is-instant');
    };

    const setCount = (p, n, bump = true) => {
      p.count.textContent = String(n);
      p.unit.textContent = n === 1 ? one : many;
      if (bump) {
        p.count.classList.remove('is-bump');
        void p.count.offsetWidth;
        p.count.classList.add('is-bump');
      }
    };
    // Step i (from 0) happens now: it lights up, the ones before are done.
    const step = (p, i) => {
      p.steps.forEach((el, n) => {
        el.classList.toggle('is-done', n < i);
        el.classList.toggle('is-now', n === i);
      });
      setCount(p, i + 1);
    };
    const finish = (p) => {
      p.steps.forEach((el) => {
        el.classList.add('is-done');
        el.classList.remove('is-now');
      });
      p.side.classList.add('is-done');
    };
    // The effect is on the clip: its fx badge lights, Effect Controls lists
    // it, and the picture (or the waveform) shows it.
    const applied = (p) => {
      p.side.classList.add('is-applied');
      p.clip.classList.add('is-fx');
      p.newRows[round].classList.add('is-on');
      p.side.dataset.look = ROUNDS[round].look;
    };
    const apply = (p) => {
      applied(p);
      p.side.classList.add('is-flash');
      p.clip.classList.remove('is-hit');
      void p.clip.offsetWidth;
      p.clip.classList.add('is-hit');
      window.setTimeout(() => p.side.classList.remove('is-flash'), 450);
    };

    // ---- Which example ----
    const setRound = (k) => {
      round = k;
      [L, R].forEach((p) => {
        p.stepLists.forEach((list, n) => {
          list.classList.toggle('is-current', n === k);
          if (n === k) list.removeAttribute('aria-hidden');
          else list.setAttribute('aria-hidden', 'true');
        });
        p.steps = [...p.stepLists[k].querySelectorAll('[data-br-step]')];
      });
      keys = R.steps.map((el) => [...el.querySelectorAll('kbd')]);
      ghost.textContent = target(k).textContent;
      picks.forEach((btn, n) => {
        btn.classList.toggle('is-current', n === k);
        btn.setAttribute('aria-pressed', String(n === k));
      });
    };
    // The lit pill fills while its example plays.
    const fillTo = (full, ms) => {
      picks.forEach((btn, n) => {
        const fill = btn.querySelector('[data-br-fill]');
        if (!fill) return;
        fill.style.transition = 'none';
        fill.style.transform = n === round && full && !ms ? 'scaleX(1)' : '';
        if (n === round && ms) {
          void fill.offsetWidth;
          fill.style.transition = `transform ${ms}ms linear`;
          fill.style.transform = 'scaleX(1)';
        }
      });
    };

    // ---- The Effects panel ----
    const showEffects = (on) => {
      tabProject.classList.toggle('is-current', !on);
      tabEffects.classList.toggle('is-current', on);
      viewProject.classList.toggle('is-current', !on);
      viewEffects.classList.toggle('is-current', on);
    };
    const twirl = (key, on) => {
      node(key).classList.toggle('is-open', on);
      kidsOf(key).classList.toggle('is-open', on);
    };
    // How far the list has to scroll for the effect to sit comfortably in
    // view (the panel is short, as it is in Premiere's Editing layout).
    const scrollNeeded = (el) => {
      const row = el.offsetHeight || 16;
      const top = el.getBoundingClientRect().top - tree.getBoundingClientRect().top;
      // whole rows, so no row is left cut at the top
      return Math.max(0, Math.ceil((top - treebox.clientHeight + row * 3.5) / row) * row);
    };
    // How far to scroll for a folder to sit up to two rows from the top,
    // with room below it for the effect inside, once it is twirled open.
    const scrollAbove = (el, fx) => {
      const row = el.offsetHeight || 16;
      const top = el.getBoundingClientRect().top - tree.getBoundingClientRect().top;
      const kids = el.nextElementSibling ? [...el.nextElementSibling.children] : [];
      const below = kids.indexOf(fx) + 1;
      const fits = Math.floor(treebox.clientHeight / row);
      const above = Math.max(0, Math.min(2, fits - below - 1));
      return Math.max(0, Math.round(top / row - above) * row);
    };
    const scrollTree = (px, instant) => {
      tree.classList.toggle('is-instant', !!instant);
      tree.style.transform = px ? `translateY(${-px}px)` : '';
    };

    const playOld = async (r) => {
      const p = L;
      const { top, sub, scrollFirst } = ROUNDS[round];
      const fx = target(round);
      // twirl open the sub-folder (step n)
      const openSub = async (n) => {
        await moveTo(r, p, node(sub).querySelector('.br-twisty'), 0.5, 0.5, T.sub);
        await click(r, p);
        twirl(sub, true);
        step(p, n);
        await wait(r, T.subRead);
      };
      // scroll down the list (step n) to the effect, or to the sub-folder
      const scrollDown = async (n) => {
        await moveTo(r, p, treebox, 0.55, 0.62, T.box);
        scrollTree(scrollFirst ? scrollAbove(node(sub), fx) : scrollNeeded(fx));
        step(p, n);
        await wait(r, T.scrolled);
      };
      await wait(r, T.start);
      await moveTo(r, p, tabEffects, 0.5, 0.55, T.tab);
      await click(r, p);
      showEffects(true);
      step(p, 0);
      await wait(r, T.tabRead);
      await moveTo(r, p, node(top).querySelector('.br-twisty'), 0.5, 0.5, T.top);
      await click(r, p);
      twirl(top, true);
      step(p, 1);
      await wait(r, T.topRead);
      if (scrollFirst) {
        await scrollDown(2);
        await openSub(3);
      } else {
        await openSub(2);
        await scrollDown(3);
      }
      await moveTo(r, p, fx, 0.42, 0.5, T.fx);
      await click(r, p);
      fx.classList.add('is-sel');
      p.cursor.classList.add('is-drag');
      step(p, 4);
      await wait(r, T.grab);
      await moveTo(r, p, p.clip, 0.5, 0.55, T.drag);
      p.clip.classList.add('is-drop');
      await wait(r, T.drop);
      p.cursor.classList.remove('is-drag');
      p.clip.classList.remove('is-drop');
      apply(p);
      await wait(r, T.settle);
      finish(p);
    };

    // ---- BetterFX ----
    // Only the effect being applied is spelled out; the other rows and the
    // category rows are plain lines, as long as their names would be.
    const line = (text, px) => `<span class="br-ln" style="--w: ${Math.min(px, 34 + text.length * px / 24)}px"></span>`;
    const render = (q) => {
      const want = ROUNDS[round].name;
      const list = LISTS[q] || [];
      let cats = 0;
      let rows = 0;
      let preset = false;
      results.innerHTML = list
        .map((item) => {
          if (Array.isArray(item)) {
            cats += 1;
            preset = item === P;
            return `<div class="br-bfx-cat">${line(catLabel[item[0]] || item[0], 120)}</div>`;
          }
          rows += 1;
          const sel = rows === 1 ? ' is-sel' : '';
          if (item !== want) return `<div class="br-bfx-row${sel}">${line(item, 230)}</div>`;
          const tag = preset ? `<span class="br-bfx-tag">${esc(tagPreset)}</span>` : '';
          return `<div class="br-bfx-row${sel}"><span>${esc(item)}</span>${tag}</div>`;
        })
        .join('');
      results.style.maxHeight = `${Math.min(cats, 2) * 26 + Math.min(rows, 5) * 40}px`;
      query.textContent = q;
      placeholder.style.display = q ? 'none' : '';
    };
    // Opens centred on the pointer, with the pointer on the search field,
    // kept inside the window with room for the results below (lib.rs).
    const openBar = () => {
      const w = R.win.clientWidth;
      const h = R.win.clientHeight;
      const s = Math.min(0.7, Math.max(0.6, (w * 0.62) / BAR_W));
      const [cx, cy] = spot(R, R.clip, 0.62, 0.55);
      const x = Math.max(6, Math.min(w - BAR_W * s - 6, cx - (BAR_W * s) / 2));
      const y = Math.max(6, Math.min(h - BAR_MAX * s - 6, cy - (BAR_COLLAPSED * s) / 2));
      bar.style.setProperty('--bs', s.toFixed(3));
      bar.style.setProperty('--bx', `${Math.round(x)}px`);
      bar.style.setProperty('--by', `${Math.round(y)}px`);
      render('');
      bar.classList.add('is-open');
    };
    const closeBar = () => bar.classList.remove('is-open');
    const down = (kbd, on) => kbd && kbd.classList.toggle('is-down', on);

    const playNew = async (r) => {
      const p = R;
      const { typed } = ROUNDS[round];
      await wait(r, 650);
      // ⌥ Space / Alt Space
      down(keys[0][0], true);
      await wait(r, 110);
      down(keys[0][1], true);
      openBar();
      step(p, 0);
      await wait(r, 240);
      keys[0].forEach((k) => down(k, false));
      await wait(r, 800);
      // type the letters
      step(p, 1);
      for (let i = 0; i < typed.length; i += 1) {
        down(keys[1][i], true);
        render(typed.slice(0, i + 1));
        await wait(r, 150);
        down(keys[1][i], false);
      }
      await wait(r, 850);
      // Enter: applied, and the bar hides at once
      down(keys[2][0], true);
      closeBar();
      step(p, 2);
      await wait(r, 160);
      down(keys[2][0], false);
      apply(p);
      await wait(r, 420);
      finish(p);
    };

    // Both sides fresh for the current example: its clip selected, the
    // Project panel showing, every folder twirled closed.
    const clear = () => {
      [L, R].forEach((p) => {
        p.side.classList.remove('is-applied', 'is-done', 'is-flash');
        delete p.side.dataset.look;
        p.stepLists.forEach((list) => list.querySelectorAll('[data-br-step]').forEach((el) => el.classList.remove('is-done', 'is-now')));
        p.cursor.classList.remove('is-drag', 'is-click');
        p.clip.classList.remove('is-drop', 'is-fx', 'is-hit');
        p.newRows.forEach((el) => el.classList.remove('is-on'));
        p.win.classList.remove('is-fading');
        setCount(p, 0, false);
      });
      showEffects(false);
      opens.forEach((el) => el.classList.remove('is-open'));
      allKids.forEach((el) => el.classList.remove('is-open'));
      targets.forEach((el) => el.classList.remove('is-sel'));
      scrollTree(0, true);
      closeBar();
      render('');
      allKeys.forEach((k) => down(k, false));
      rest(L);
      rest(R);
    };

    const play = async (from) => {
      run += 1;
      const r = run;
      playing = true;
      setRound(from);
      clear();
      try {
        for (;;) {
          fillTo(true, OLD_MS + HOLD);
          await Promise.all([playOld(r), playNew(r)]);
          await wait(r, HOLD);
          L.win.classList.add('is-fading');
          R.win.classList.add('is-fading');
          await wait(r, FADE);
          setRound((round + 1) % ROUNDS.length);
          fillTo(false);
          clear();
          await wait(r, GAP);
        }
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    const stop = () => {
      run += 1;
      playing = false;
      fillTo(false);
      clear();
    };

    // How an example ends: the panel open at the effect, the effect on both
    // clips, every step lit, both done.
    const finished = (k) => {
      setRound(k);
      clear();
      const { top, sub, scrollFirst } = ROUNDS[k];
      showEffects(true);
      twirl(top, true);
      twirl(sub, true);
      scrollTree(scrollFirst ? scrollAbove(node(sub), target(k)) : scrollNeeded(target(k)), true);
      target(k).classList.add('is-sel');
      [L, R].forEach((p) => {
        applied(p);
        finish(p);
        setCount(p, p.steps.length, false);
      });
      fillTo(true);
    };

    // A pill plays its example from the start (or shows how it ends).
    picks.forEach((btn) => {
      btn.addEventListener('click', () => {
        const k = Number(btn.dataset.brPick) || 0;
        if (!animated) {
          finished(k);
        } else if (onScreen && !document.hidden) {
          play(k);
        } else {
          setRound(k);
          clear();
        }
      });
    });

    if (still.matches || !('IntersectionObserver' in window)) {
      finished(0);
      return;
    }

    animated = true;
    setRound(0);
    clear();
    const sync = () => {
      if (onScreen && !document.hidden) {
        if (!playing) play(round);
      } else if (playing) {
        stop();
      }
    };
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          onScreen = entry.isIntersecting;
        });
        sync();
      },
      { threshold: 0.3 }
    ).observe(stage);
    document.addEventListener('visibilitychange', sync);
  };

  const boot = () => document.querySelectorAll('[data-bfx-race]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
